/**
 * Appendix-L portal capability & permission matrix — AS CODE (W5-11).
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * v2.2 Appendix L ("External portal capability and permission matrix starter",
 * `docs/architecture/MLG_Group_Operating_System_Architecture_v2.2.md:4001`) asks
 * every subsidiary to complete a capability matrix per external role and case
 * type, and states the rule that governs the blanks: **"Blank cells are
 * deny-by-default."**
 *
 * Until now that matrix lived in three places at once and in none of them
 * completely: a prose table in `FASE5-WORKPLACE-VERKENNING.md:56-66`, the
 * presence of a route in some controller, and the presence of a `<Link>` in one
 * of the two portal shells. Each could drift from the other two silently — and
 * two of them had (a `/sicherheit` link the Verkauf shell never rendered, a
 * download route the Hypothek customer could not reach). The verkenning's own
 * risk 5 puts it plainly: the matrix may not silently widen any capability.
 *
 * So the matrix becomes DATA, and three consumers read the same data:
 *
 *   1. the portal shells derive their navigation from it (`portalShellLinks`),
 *      which is why "Kontosicherheit belongs to EVERY portal path" is now a
 *      property of the registry instead of a comment above one of the two
 *      shells;
 *   2. the backend pins its customer-reachable route inventory against it
 *      (`apps/backend/src/me/portal-capabilities.contract.test.ts`), which is
 *      what makes deny-by-default mechanical rather than aspirational: a new
 *      route without `@Roles` is customer-reachable the moment it is merged,
 *      and that test turns red until someone puts it in this file on purpose;
 *   3. the isolation regression package (W5-12) asserts the nine Appendix-L
 *      portal invariants against it.
 *
 * WHAT THIS FILE IS NOT
 * ---------------------
 * It is not an authorisation mechanism. Server-side `@Roles` + RLS + the
 * per-service account scoping remain the only enforcement — nav and matrix are
 * presentation and documentation, exactly as `apps/crm/src/products/registry.ts`
 * says of the CRM nav. What the matrix adds is that a drift between the three
 * becomes a build failure instead of a discovery.
 *
 * Node-free on purpose: the portal imports it via `@finavest/shared/client`.
 */
// ---------------------------------------------------------------------------
// 1. Surfaces and audience — which portal surfaces which customer kind sees
// ---------------------------------------------------------------------------
/**
 * The portal's case surfaces. These names are the WIRE CONTRACT of
 * `GET /me/context` (`apps/backend/src/me/portal-surface.registry.ts`) and of
 * `PortalSurfaceProvider.surface`; they are core vocabulary while the tables
 * behind them are product knowledge (D11).
 *
 * `mitwirkung` (DF-6, DRIP-FEEDBACK-LOOP.md §5.3): participation in a project
 * of your own company — the feedback-loop surface for a dochter-teilnehmer.
 * The name is deliberately PRODUCT-NEUTRAL: `packages/shared` is copied into
 * every commercial extraction of Makler or Hypothek, and a surface literally
 * called `drip` would put the internal governance instrument by name into a
 * sold package — the exact thing `no-product-to-drip` protects, and a string
 * escapes that CI rule. That DRIP is the only filler of this surface is
 * product knowledge and stays behind the `PortalSurfaceRegistry` slot. The
 * BRAND shown to the participant (G-5, still an owner decision) lives in the
 * portal's label registry, never here.
 *
 * `bau` (Eigentuemerportal) is a different KIND of case than dossier and
 * verkauf: those belong to a customer who is the subject of the case, while a
 * Bauherrschaft is the CLIENT of a construction project the MLG runs. That
 * difference is why its capability column below is almost entirely `denied` —
 * it reads, it does not act.
 */
export const PORTAL_SURFACES = ['dossier', 'verkauf', 'mitwirkung', 'bau'];
/**
 * What STANDING an account has on a surface — the three-valued answer to a
 * question that used to be a boolean (Codex remediation DRIP-012).
 *
 *   `none`    — nothing here, and there never was: the stranger.
 *   `active`  — a live case or participation; every affordance applies.
 *   `history` — no live case, but this account HAS a past on this surface and
 *               keeps READ access to it. The participation surface is the
 *               first with that shape: the owner decided on 17-08 (DF-8
 *               MAJOR-1, variant B) that a revoked participant keeps reading
 *               their own submissions and the staff thread under them, and the
 *               database was already built that way — the klant-SELECT policy
 *               on `drip_eingaben` gates on `melderAccountId` ALONE, with no
 *               living filter.
 *
 * WHY THE DISTINCTION IS PART OF THE CONTRACT AND NOT OF ONE SCREEN. A
 * boolean forces the surface to say the same thing to somebody who never
 * participated and to somebody whose participation ended — and those are
 * different sentences, different navigation and a different set of write
 * affordances. Vocabulary here, product knowledge behind the
 * `PortalSurfaceProvider` slot, exactly like the surface names themselves.
 */
export const PORTAL_SURFACE_ZUSTAENDE = ['none', 'active', 'history'];
/**
 * The account surface every authenticated customer has, whatever their case
 * type: login security (F2d TOTP) and the routing brain. Appendix L has no row
 * for it because it is not a case capability — Appendix K's "Security" row owns
 * it. Modelled explicitly so a shell can never render a case surface WITHOUT
 * it, which is precisely the `/sicherheit` gap W5-11 closes.
 */
export const PORTAL_ACCOUNT_SURFACE = 'konto';
export function portalCustomerKind(audience) {
    if (audience.hasMandate && audience.hasFinancing)
        return 'hybrid';
    if (audience.hasMandate)
        return 'seller';
    if (audience.hasFinancing)
        return 'borrower';
    if (audience.hasMitwirkung)
        return 'mitwirkend';
    return 'none';
}
/**
 * Surfaces per kind. `none` gets the empty list, NOT a default surface: the
 * landing fallback is a routing decision (`landingPath`), not a grant.
 */
export const PORTAL_KIND_SURFACES = {
    seller: ['verkauf'],
    borrower: ['dossier'],
    hybrid: ['verkauf', 'dossier'],
    mitwirkend: ['mitwirkung'],
    none: [],
};
/**
 * Case surfaces from the kind, plus the additive surfaces APPENDED.
 *
 * `mitwirkung` when the account participates alongside a case — a seller who
 * is also the dochter's verantwortliche sees both, sales first. Appended
 * rather than modelled as more kinds: participation is orthogonal to what
 * case you own.
 *
 * `bau` likewise ALONGSIDE the kind mapping rather than folded into it:
 * `PortalCustomerKind` answers "is this person selling or borrowing", and a
 * Bauherrschaft is neither — the same household can be a seller AND have a
 * house being built. Folding either flag in would have meant a combinatorial
 * enum (`seller_bauherr`, …) whose only job is to be exploded again by the
 * next surface.
 */
export function portalSurfacesFor(audience) {
    const surfaces = [...PORTAL_KIND_SURFACES[portalCustomerKind(audience)]];
    if (audience.hasMitwirkung && !surfaces.includes('mitwirkung'))
        surfaces.push('mitwirkung');
    if (audience.hasBauProjekt)
        surfaces.push('bau');
    return surfaces;
}
/**
 * The surface an account lands on. Mirrors `MeService.getContext` exactly: a
 * mandate owner is primarily a SELLER; then the borrower's dossier; then — new
 * with DF-6 — the participation surface for an account that owns nothing else;
 * and `none` keeps the historical /dossier default, where the existing
 * dossier→verkauf self-heal is the behaviour the portal already relies on.
 * A CASE always outranks participation: the feedback loop is a side door of
 * the working relationship, never the front door of a paying case.
 */
export function portalPrimarySurface(audience) {
    if (audience.hasMandate)
        return 'verkauf';
    // #378 MAJOR-1: EIN FALL SCHLÄGT EINE TEILNAHME. Diese Zeile fehlte, und der
    // Kommentar darüber sagte «an account whose ONLY case is a building
    // project» — geprüft wurde das nie. Folge: ein Kreditnehmer mit laufendem
    // Finanzierungsdossier, dem die MLG einen Bauzugang erteilt, landete ab
    // diesem Moment beim Login auf /bau statt auf seinem Dossier. Sein Dossier
    // war nicht weg, aber es war nicht mehr sein Zuhause. Die Asymmetrie war
    // auch in sich unstimmig: `hasMandate` schlug `bau`, `hasFinancing` nicht,
    // obwohl beides zahlende Fälle sind.
    if (audience.hasFinancing)
        return 'dossier';
    // Erst jetzt: ein Konto, dessen EINZIGER Fall ein Bauprojekt ist, würde
    // sonst auf einem leeren Hypothekendossier landen — der Selbstheilungs-
    // Bounce kennt nur verkauf. `bau` vor `mitwirkung` nach derselben Regel,
    // die beide hinter die Fallflächen stellt: eine Bauherrschaft ist zahlender
    // AUFTRAGGEBER eines Projekts, die Teilnahme die Seitentür einer
    // Arbeitsbeziehung — a case always outranks participation.
    if (audience.hasBauProjekt)
        return 'bau';
    if (audience.hasMitwirkung)
        return 'mitwirkung';
    return 'dossier';
}
export function portalSees(audience, surface) {
    return portalSurfacesFor(audience).includes(surface);
}
// ---------------------------------------------------------------------------
// 2. Portal routes — the shells' single source of navigation truth
// ---------------------------------------------------------------------------
/**
 * Stable keys the shells map onto their own labels (i18n or literal). Keys, not
 * labels, because the registry is shared with the backend and must not carry
 * German copy into a route-inventory test.
 */
export const PORTAL_ROUTE_KEYS = [
    'dossier.overview',
    'verkauf.overview',
    'verkauf.contract',
    'mitwirkung.overview',
    'mitwirkung.sicherheit',
    'bau.overview',
    'konto.security',
];
/**
 * Every route a portal SHELL links to. Deliberately not every portal route: the
 * dossier stepper (`/dossier/<step>`, `/dossier/dokumente`, `/dossier/einreichen`)
 * is generated from the dossier data model in `apps/portal/src/dossier/steps.ts`
 * and stays there — a static copy here would be a second truth that drifts the
 * first time a step becomes conditional. What the registry owns is the part
 * that is a GRANT: which surfaces exist, and which of them this account sees.
 *
 * ASYMMETRY, DECLARED (W5-11 review, F4): `VerkaufShell` consumes
 * `links.surface`, `PortalShell` does not — its own navigation IS that stepper,
 * so `dossier.overview` is rendered by the stepper's first item rather than from
 * this table. The entry flag on it is therefore not decoration: it is what the
 * OTHER shell cross-links to, which is the only use an entry route has. Written
 * down because a key with no consumer in its own shell reads like an oversight,
 * and the fix for that appearance would be to duplicate the stepper here — which
 * is the second truth this comment exists to prevent.
 */
export const PORTAL_ROUTES = [
    { key: 'dossier.overview', path: '/dossier', surface: 'dossier', entry: true },
    { key: 'verkauf.overview', path: '/verkauf', surface: 'verkauf', entry: true },
    { key: 'verkauf.contract', path: '/verkauf/vertrag', surface: 'verkauf', entry: false },
    // DF-6: the participation entry. The route GROUP behind it lands in DF-9 —
    // harmless in between, because a shell only renders this link for an
    // audience whose `hasMitwirkung` is true, and that flag has no producer
    // until DF-7's invites exist. Deeper mitwirkung routes stay OUT of this
    // table for the same reason the dossier stepper does: DF-9's own navigation
    // owns them, and a static copy here would be the second truth.
    { key: 'mitwirkung.overview', path: '/mitwirkung', surface: 'mitwirkung', entry: true },
    // W5 (DRIP-ONTVLECHTING §4, DF-10): the participation surface's OWN security
    // door — same TOTP machinery as /sicherheit, DRIP face, only on the DRIP
    // host. In THIS table (unlike the deeper mitwirkung routes) because it is an
    // ACCOUNT concern: "Kontosicherheit gehört zu JEDEM Portal-Weg" is the
    // registry's founding sentence, and since W5 the participation surface can
    // finally honour it without linking a 404.
    {
        key: 'mitwirkung.sicherheit',
        path: '/mitwirkung/sicherheit',
        surface: 'mitwirkung',
        entry: false,
    },
    { key: 'bau.overview', path: '/bau', surface: 'bau', entry: true },
    { key: 'konto.security', path: '/sicherheit', surface: PORTAL_ACCOUNT_SURFACE, entry: false },
];
/**
 * The complete link set for one shell. The three groups are returned separately
 * because the two shells place them differently (the Verkauf shell has a
 * horizontal nav under its topbar, the dossier shell a progress stepper in a
 * sidebar) — but WHICH links exist is one decision, taken here.
 *
 * `account` is unconditional FOR EVERY SURFACE — again, since W5. That is the
 * original point: the comment `PortalShell.tsx` already carried
 * ("Kontosicherheit gehoert zu JEDEM Portal-Weg") could not be enforced while
 * each shell decided for itself. S-4 made the participation surface a
 * temporary exception (customer-portal /sicherheit is a 404 on the split DRIP
 * host, and a nav link to a 404 is worse than the gap it papers over); W5
 * resolves it the right way round — the surface carries its OWN door,
 * /mitwirkung/sicherheit, same machinery behind a DRIP face.
 */
export function portalShellLinks(active, audience) {
    const owned = portalSurfacesFor(audience);
    // S-4 (DRIP-ONTVLECHTING.md besluit 1+2): the participation surface is its
    // own programme on its own host, so it takes no part in cross-surface
    // traffic IN EITHER DIRECTION — a customer shell never links to
    // /mitwirkung (404 on the customer host once split), and the participation
    // shell never links to a case surface or to customer-portal /sicherheit
    // (404 on the DRIP host). This is deliberately UNCONDITIONAL — not
    // host-aware — because a link that only sometimes exists is the harder bug
    // to see, and on the unsplit stack the removed links merely disappear from
    // nav while every route keeps working.
    //
    // W5 closes the G-3 activation concern S-4 left open: the account group is
    // no longer empty but carries the surface's OWN security door
    // (/mitwirkung/sicherheit) — the registry's founding sentence
    // ("Kontosicherheit gehört zu JEDEM Portal-Weg") holds again for every
    // surface, each behind its own host.
    if (active === 'mitwirkung') {
        return {
            // De eigen deur hoort in ÉÉN groep (review #408, m2): hij is een
            // account-link, geen surface-nav — en de selectie is SEMANTISCH (per
            // sleutel), niet structureel («elke non-entry»): een toekomstige diepe
            // mitwirkung-route zou anders stil een topbar-link worden, terwijl de
            // tabelcomment juist belooft dat diepere routes buiten blijven.
            surface: PORTAL_ROUTES.filter((r) => r.surface === active && r.key !== 'mitwirkung.sicherheit'),
            cross: [],
            account: PORTAL_ROUTES.filter((r) => r.key === 'mitwirkung.sicherheit'),
        };
    }
    return {
        surface: PORTAL_ROUTES.filter((r) => r.surface === active),
        cross: PORTAL_ROUTES.filter((r) => r.entry &&
            r.surface !== active &&
            r.surface !== 'mitwirkung' &&
            owned.includes(r.surface)),
        account: PORTAL_ROUTES.filter((r) => r.surface === PORTAL_ACCOUNT_SURFACE),
    };
}
// ---------------------------------------------------------------------------
// 3. The Appendix-L capability matrix
// ---------------------------------------------------------------------------
/**
 * The ten Appendix-L capability rows, verbatim in order (v2.2:4007-4018).
 * The list is closed: a capability outside it is not a blank cell, it is a
 * capability nobody has decided about.
 */
export const PORTAL_CAPABILITIES = [
    'view_case_status',
    'download_documents',
    'upload_documents',
    'complete_checklist',
    'edit_master_data',
    'secure_message',
    'approve_reject',
    'sign_document',
    'invite_participant',
    'export_dossier',
];
const DENIED = (control) => ({ state: 'denied', api: [], control });
const session = (...routes) => routes.map((route) => ({ route, auth: 'customer-session' }));
/**
 * The matrix. Rows are Appendix-L capabilities, columns are this estate's two
 * live external roles. Read it as the answer to "what may the owner of THIS
 * kind of case do", and note that every non-`allowed` cell carries its reason
 * in `control` — a blank cell with no explanation is how a matrix rots.
 */
export const PORTAL_CAPABILITY_MATRIX = {
    // ── Customer as BORROWER (Finavest Hypothek, /dossier) ────────────────────
    dossier: {
        view_case_status: {
            state: 'allowed',
            api: session('GET /me/financing-requests', 'GET /me/financing-requests/offen', 'POST /me/financing-requests', 'GET /financing-requests/:id/dossier'),
            control: 'Case membership: financing_requests.customerAccountId under customer RLS. `GET .../offen` is de BOOTVRAAG van het portaal en schrijft per definitie niets (owner-order 31-8): tot dan deed het dossierscherm bij elk paginabezoek de POST hieronder, en die is een «ensure» — een paginabezoek fabriceerde dus een dossier. De POST is nu alleen nog de CTA-klik en draagt de sale-only-poort, die sinds diezelfde order naar de TOESTAND van een bestaand dossier kijkt (teruggetrokken/afgewezen ontwapent haar niet meer) en alleen wijkt voor een expliciete `absicht`. `GET .../offen` draagt sinds verdict #520 MAJOR-2 óók de S-4-weigering: een deelnemer-ONLY account (geen mandaat, geen levend dossier, wél een lopende mitwirkung) krijgt 403 `mitwirkung_only_account` op de bootvraag en wordt door het portaal naar `/` gestuurd — zijn ingang is de mitwirkung-host. Een mandaatklant wordt NIET geweigerd: voor hem is `null` + CTA de bedoelde ingang. Sinds de na-PR van 1-9 (hermeting #520, registerpunt 1) stellen bootvraag, POST-snelpad en poort ÉÉN vraag: «is er een dossier buiten withdrawn/rejected?» — een ingediend dossier wordt dus ook door de kale POST teruggegeven, nooit verdubbeld.',
        },
        download_documents: {
            state: 'limited',
            api: session('GET /me/documents/:id/file', 'GET /financing-requests/:id/documents'),
            control: 'OWN uploads only (uploadedByKind = customer) and only once the AV scan is clean. Staff uploads on the same request are invisible on BOTH paths: the stream refuses them, and since the W5-11 review the LISTING filters them too — a name like INTERN-bonitaet-negativ.pdf is itself the disclosure, so refusing only the bytes was a formality. The requirement row still reports status=uploaded, so a staff-fulfilled requirement is not re-requested: the FACT is the customer’s, the ARTEFACT is not. Deliberately NARROWER than the verkauf column, and that is a product decision — a financing dossier never carried a bidirectional-documents requirement, so deny-by-default holds until one is taken. Session-scoped stream, never a presigned URL.',
        },
        upload_documents: {
            state: 'allowed',
            api: session('POST /financing-requests/:id/documents', 'GET /financing-requests/:id/documents'),
            control: 'Magic-byte sniff + MIME allow-list + size cap, ClamAV before the object is stored, SHA-256, owning request resolved under customer RLS.',
        },
        complete_checklist: {
            state: 'allowed',
            api: session('PUT /financing-requests/:id/dossier/:section'),
            control: 'Versioned per section (optimistic concurrency); a submitted dossier is immutable for the customer (dossier_locked).',
        },
        edit_master_data: {
            state: 'allowed',
            api: session('PUT /financing-requests/:id/dossier/:section'),
            control: 'Own dossier only, schema-validated per section. Unlike the Verkauf side there is no review gate: the dossier IS the customer’s own submission until it is submitted.',
        },
        secure_message: DENIED('No case-bound thread on the Hypothek side. The portal has no message surface at all; correspondence runs through the advisor’s mailbox.'),
        approve_reject: DENIED('No approvable item exists on a financing dossier today (the bank shortlist is staff-only by policy — GAP-7 in the RLS suite).'),
        sign_document: {
            state: 'limited',
            api: session('POST /financing-requests/:id/submit'),
            control: 'Submission is a declaration of intent, not a signature: no recognised e-signature provider is bound (owner-gate). Recorded as `limited` rather than `allowed` so the gap stays visible.',
        },
        invite_participant: DENIED('Staff-initiated only: a second borrower is invited from the CRM, never from the portal.'),
        export_dossier: DENIED('Appendix L deny-by-default. A full-dossier export needs a separate documented purpose and approval.'),
    },
    // ── OWNER / SELLER (MLG Verkauf, /verkauf) ────────────────────────────────
    verkauf: {
        view_case_status: {
            state: 'allowed',
            api: session('GET /me/mandates', 'GET /me/mandates/:id'),
            control: 'Case membership: mandates.customerAccountId under customer RLS plus a deletedAt tombstone guard, so a mandate in the Papierkorb disappears from the portal.',
        },
        download_documents: {
            state: 'allowed',
            api: session('GET /mandates/:id/documents', 'GET /mandate-documents/:id/file'),
            control: 'BIDIRECTIONAL BY DECISION (F6, owner requirement at portal build): the owner uploads their own Beilagen AND sees the documents the makelaar uploaded in the CRM — Grundbuchauszug, Schätzung, Baupläne, Mieterspiegel and the rest of the Beilage-1 catalogue. That is the point of a mandate portal, and it is why this cell is `allowed` where the dossier column is `limited`: there is no uploadedByKind filter here and there is not supposed to be one. Every docKey in DOC_REQUIREMENTS is a property document the owner is entitled to; OWNER_VISIBLE_DOC_GROUPS + portalNonOwnerVisibleDocKeys() pin that there is no internal category, so adding one turns this cell red instead of silently widening it. Bounds that DO apply: same-origin session-scoped stream (MinIO stays internal), clean-scan gate, parent tombstone guard, `private, no-store`, and every download audited.',
        },
        upload_documents: {
            state: 'allowed',
            api: session('POST /mandates/:id/documents'),
            control: 'Beilage-1 requirement key validated against the shared DOC_REQUIREMENTS list; same sniff/AV/checksum pipeline as the Hypothek side.',
        },
        complete_checklist: {
            state: 'limited',
            api: session('GET /me/mandates/:id'),
            control: 'READ-ONLY. The checklist is the makelaar’s working document; the owner sees it and proposes corrections through the approval gate below.',
        },
        edit_master_data: {
            state: 'limited',
            api: session('POST /me/mandates/:id/changes'),
            control: 'Allow-listed sections/fields (`checkProposable` in @finavest/shared — the same policy the portal UI renders from) + staff review gate: a proposal is never a canonical write. Rate-limited and capped per mandate.',
        },
        secure_message: {
            state: 'limited',
            api: session('POST /me/mandates/:id/changes'),
            control: 'There is no thread — only the free `note` field carried by a correction proposal. Recorded as `limited` so it is not mistaken for a messaging capability.',
        },
        approve_reject: {
            state: 'allowed',
            api: session('GET /me/mandates/:id/shooting', 'POST /me/mandates/:id/shooting/:shootingId/approve', 'POST /me/mandates/:id/shooting/:shootingId/decline'),
            control: 'Only a `proposed` shooting is respondable (state check), the decision is immutable in the row and audited as `shooting.customer_approved` / `shooting.customer_declined`.',
        },
        sign_document: {
            state: 'limited',
            api: [
                ...session('GET /me/mandates/:id/contract', 'POST /me/mandates/:id/contract/pdf', 'POST /me/mandates/:id/contract/sign'),
                { route: 'GET /contract/sign/:token', auth: 'signed-token' },
                { route: 'POST /contract/sign/:token', auth: 'signed-token' },
                { route: 'POST /contract/sign/:token/decline', auth: 'signed-token' },
            ],
            control: 'Own SES token, no recognised e-signature provider (owner-gate, explicitly out of scope for fase 5). The signed snapshot + PDF are the evidence package and become visible to the owner only once the contract leaves `draft` — the estate’s one deliberate document-sharing state. SINCE PR-3 THIS CELL CARRIES A SESSION-BOUND WRITE (owner dossier §3.3): `POST /me/mandates/:id/contract/sign` lets an owner who ALREADY holds a portal account sign from `/verkauf/vertrag` instead of hunting for the mail link. Same legal act, same finalize core as the token route — what differs is the proof of WHO signed (the token proves "holder of this link", the session proves "this account"), so the `contract.signed` audit row always names the signing account as actor — on the token route that column carries whatever account the signature happened to BIND, and is usually empty. Three controls carry it: the ownership read runs under customer RLS (foreign mandate / unbound contract → 404, never 403), the atomic sent→signed claim carries `customerAccountId` IN ITS WHERE so the boundary survives even a loosened read, and signing here CONSUMES every open sign token of that contract — a live bearer link left in a mailbox would otherwise keep serving the full contract text after the signature.',
        },
        invite_participant: DENIED('Co-owners are invited from the CRM. Appendix L rates this "Rare/limited" and requires a relationship policy, expiry and an approver; none exists.'),
        export_dossier: DENIED('Appendix L deny-by-default. Per-document download is the granted capability; a bulk case export is not.'),
    },
    // ── PARTICIPANT of the feedback loop (DF-6 frame, DF-8 projection) ────────
    //
    // The column landed all-denied in DF-6 (the door frame) and DF-8 opened the
    // first three cells TOGETHER WITH their routes — the two-way contract test
    // (matrix route ⇔ real unguarded controller) never sees a promise without
    // an implementation or the reverse. Invariant D-C binds every grant here:
    // the external surface reads a narrow PROJECTION (`/me/drip/…`) composed by
    // the DRIP service under CUSTOMER RLS — the discovery tables stay
    // staff-only, so the projection could not widen silently if it tried.
    mitwirkung: {
        view_case_status: {
            state: 'limited',
            api: session('GET /me/drip/projekt', 'GET /me/drip/eingaben', 'GET /me/drip/eingaben/:id'),
            control: 'PROJECTION under customer RLS (D-C): the melder reads their OWN points and mirror thread only; MLG triage fields (schwere, assignment, corrected art) are structurally absent from the wire shape (§7.4 — "een melder die S4 ziet, leest een oordeel over zichzelf"), verlauf authors surface as a KIND (mlg|teilnehmer), never an id, and the dossier title is unreadable by construction (staff-only RLS). REVOCATION IS NOT AMNESIA, and the split is deliberate (owner decision 17-08, variant B): a revoked account can no longer WRITE and has NO active project — `GET /me/drip/projekt` comes back empty because that policy does gate on a living participation — but it KEEPS READ ACCESS TO ITS OWN EARLIER SUBMISSIONS, because the klant-SELECT policy on `drip_eingaben`/`_verlauf` gates on `melderAccountId = account_id` alone (DF-1, on purpose). That is the revDSG line: the melder may see what has been recorded about them, including the staff verlauf thread that keeps growing after the revocation. The earlier wording here claimed the opposite ("a revoked account reads as never-invited") and was simply untrue of two of the three read routes; the behaviour it described was never built. Pinned by the revoked-reader tests in drip-mitwirkung.service.rls.test.ts. SINCE THE CODEX REMEDIATION (DRIP-012) THAT SPLIT IS ON THE WIRE and no longer only in the policies: the surface reports a PortalSurfaceZustand — none | active | history — on GET /me/context (mitwirkungZustand) and on GET /me/drip/projekt (zustand), so the screens can render an explicit READ-ONLY state (the history visible, every write affordance absent and SAID to be absent) instead of the sentence written for a stranger. The read grants in this cell are unchanged; what changed is that the layers above can no longer contradict them.',
        },
        download_documents: DENIED('The participant surface carries no document store of its own; what a round offers is described by its zweck, not by files. If a future round needs artefact downloads, that is a new decision against invariant D-C, not a widening of this cell.'),
        upload_documents: DENIED('A screenshot attachment over the existing ClamAV upload path is a DF-9 option (§6.2) and would enter as `limited` with its own control; until that route exists the cell stays the Appendix-L blank.'),
        complete_checklist: DENIED('No checklist exists on the participation surface: the §5.5 screens are a project mirror, a point list and an acceptance decision. A checklist would be a new product decision, not a blank to fill.'),
        edit_master_data: DENIED('A participant edits no master data: their own eingabe is frozen at submission (DF-1 trigger) and everything else on the surface is a read-only projection. Corrections travel as new points, like the verkauf change-proposal gate but simpler.'),
        secure_message: {
            state: 'limited',
            api: session('POST /me/drip/eingaben', 'POST /me/drip/eingaben/:id/verlauf'),
            control: 'NOT a mailbox, and recorded as limited so it is never mistaken for one: the intake creates a point (art, titel, text — the melder chooses no severity, ADR-052) and the verlauf route adds a case-bound note to ONE own point (§7.4 row 2). Both demand a LIVING participation at the RLS WITH CHECK, the submission freezes at the trigger (DF-1), the intake is rate-limited, and every act is audited with actorKind customer.',
        },
        approve_reject: {
            state: 'limited',
            api: session('POST /me/drip/eingaben/:id/verifikation', 'POST /me/drip/abnahmen/:id/entscheid'),
            control: 'Two acts, both bounded at the database. The melder VERDICT on their own fix: the one §7.3 pair the customer gate leaves open (zur_verifikation → verifiziert | in_bearbeitung), single-apply, mirror line in the same transaction. And since DF-10 the GO/NO-GO itself (§8.2): only the LIVING verantwortliche, only in their own name (M-4 policy), never MLG (N-1 trigger), irrevocable once set (freeze trigger — a new offer is a NEW round), server-stamped time, begruendung mandatory for go_mit_auflagen/kein_go (CHECK + schema), audited actorKind customer and notified to the accountable owner (§8.2.6). Open points are SHOWN in the confirmation, never blocking — go_mit_auflagen IS the §9.6 waiver. G-3 (TOTP for the decision) rides DRIP_GO_TOTP_PFLICHT, default off.',
        },
        sign_document: DENIED('The GO is a recorded decision (drip_abnahmen row + audit), deliberately NOT a signature: no e-signature provider is bound anywhere in the estate (owner-gate), and §8.2.2 needs an attributable decision, not a signed artefact.'),
        invite_participant: DENIED('Participants are invited by MLG staff from the CRM (DF-7), never by each other — the D-B invariant holds the same line at the database: the grant table refuses every customer write, including self-issue.'),
        export_dossier: DENIED('Appendix L deny-by-default, and sharper here than anywhere: the project mirror aggregates MLG-internal work; a bulk export of it needs a documented purpose and an owner approval that nobody has asked for.'),
    },
    // ── BAUHERRSCHAFT (MLG Generalunternehmung, /bau) ─────────────────────────
    // Eine LESEFLÄCHE, und das ist der ganze Entwurf. Der Bauherr fragt drei
    // Dinge: wie weit seid ihr, liegt ihr im Plan, und was wurde beschlossen.
    // Alles andere im Bau-Modul ist der Einkauf der MLG — Offerten, Preisspiegel,
    // Vergabeprotokolle, Werkverträge, Nachträge, Rechnungen. Wer davon etwas
    // öffnet, öffnet die Marge. Deshalb steht hier neunmal `denied` mit Grund und
    // nicht einmal «später vielleicht».
    bau: {
        view_case_status: {
            state: 'allowed',
            api: session('GET /bau/portal/projekte', 'GET /bau/portal/projekte/:id'),
            control: 'Zugehörigkeit über bau_projekt_zugaenge (Grant-Tabelle, staff-only beschreibbar) unter Kunden-RLS: die SELECT-Policy von bau_projekte/bau_termine/bau_protokolle hängt an einem LEBENDEN Zugang (validTo IS NULL OR > now()), ein Entzug wirkt in derselben Sekunde. Darüber liegt eine handgeschriebene Projektion: der Service gibt Felder EINZELN heraus, nie die Zeile — sonst wäre die nächste Spalte auf bau_projekte (heute z. B. die Standardabzüge) am Tag ihrer Migration im Portal.',
        },
        download_documents: DENIED('Kein Dokument, nur Inhalt: die Beschlüsse eines freigegebenen Protokolls sind über view_case_status lesbar, das erzeugte Protokoll-PDF ist es nicht. Der Grund liegt im Generator, nicht in der Berechtigung — er zieht die Pendenzenliste LIVE (Rollover), ein einmal «übergebenes» Dokument würde sich hinter dem Bauherrn weiter verändern. Ein stabiles Bauherren-PDF heisst Snapshot beim Freigeben und ist ein eigener Schnitt. Bewusst `denied` und nicht `limited`: eine gewährte Zelle ohne Route ist in diesem Haus ein Versprechen (I-9), und ein Versprechen ist genau das, was diese Zeile nicht sein soll.'),
        upload_documents: DENIED('Der Bauherr lädt nichts hoch. Pläne und Belege kommen von der Bauleitung und den Unternehmern; ein Upload-Pfad bräuchte Ablage, Virenkette und eine Zuständigkeit für den Inhalt — das ist der Unternehmer-Teil des Portals und ein eigener Entscheid.'),
        complete_checklist: DENIED('Checklisten und Inspektionen sind das Arbeitsblatt der Bauleitung und seit #351 nach Abschluss eingefroren. Ein Bauherr, der Punkte abhakt, würde einen Abnahmebefund unterschreiben, den er nicht erhoben hat.'),
        edit_master_data: DENIED('Projektstammdaten gehören der MLG. Eine Korrektur läuft über den Bauleiter — anders als beim Verkaufsmandat gibt es hier keinen Vorschlags-Kanal mit Prüfgatter, und deny-by-default gilt, bis einer gebaut ist.'),
        secure_message: DENIED('Kein fallbezogener Nachrichtenkanal. Korrespondenz läuft über die Bauleitung; das Bau-Modul versendet grundsätzlich nichts automatisch (rote Linie 8.4), und ein Posteingang im Portal wäre genau der automatische Kanal, den diese Linie ausschliesst.'),
        approve_reject: DENIED('NOCH nichts Freigebbares. Der offensichtliche Kandidat ist die Nachtragsfreigabe durch die Bauherrschaft — sie ist ein eigener PR wert, weil sie den ersten SCHREIBENDEN Kundenzweig des Bau-Moduls bedeutet und damit eine eigene Statusmaschine, eigene Policies (INSERT/UPDATE mit Kunden-Zweig) und eine eigene Beweislage braucht.'),
        sign_document: DENIED('Keine anerkannte E-Signatur gebunden (Owner-Gate, gilt hier wie auf der Verkaufsseite).'),
        invite_participant: DENIED('Zugänge erteilt die MLG im CRM. Ein Bauherr, der Mitbesitzer selbst einlädt, verteilt Einsicht in ein Projekt, dessen Daten nicht ihm gehören — das braucht Beziehungsnachweis, Ablauf und Freigabe, und nichts davon existiert.'),
        export_dossier: DENIED('Appendix L deny-by-default. Ein Gesamtexport des Bauprojekts gäbe es hier gar nicht zu holen — die Fläche IST die Übersicht. Sobald ein Export gewollt ist, braucht er einen eigenen Zweck, eine Aufbewahrungsfrist und eine Freigabe.'),
    },
};
/** Deny-by-default lookup: an unknown pair is `denied`, never undefined. */
export function portalCapability(surface, capability) {
    return (PORTAL_CAPABILITY_MATRIX[surface]?.[capability] ??
        DENIED('Not in the matrix — Appendix L blank cell, deny-by-default.'));
}
export function portalCan(surface, capability) {
    return portalCapability(surface, capability).state !== 'denied';
}
// ---------------------------------------------------------------------------
// 3b. The one place the verkauf column is deliberately wide, guarded
// ---------------------------------------------------------------------------
/**
 * The Beilage-1 document groups an OWNER is entitled to see.
 *
 * Why this exists at all: `verkauf.download_documents` has no `uploadedByKind`
 * filter, and that is the F6 owner requirement rather than an oversight — a
 * mandate portal whose owner cannot see the Grundbuchauszug their makelaar
 * uploaded is not a mandate portal. But "no filter" and "no boundary" are
 * different sentences, and only the first one is written in the code. Today
 * every one of the four `DOC_GROUPS` is a property-document group: Liegenschaft,
 * Neubau/Umbau, Stockwerkeigentum, Bauland. There is no internal category, so
 * there is nothing the wide grant wrongly includes.
 *
 * That is a fact about today, and facts about today rot. The moment someone adds
 * an `intern` group — a Preisstrategie, a Konkurrenzanalyse, a note about the
 * seller — the existing wide grant would carry it to the customer on the next
 * deploy, silently, with no diff anywhere near the portal. This list is what
 * makes that impossible: the pin below goes red, and the matrix cell has to be
 * revisited on purpose.
 */
export const OWNER_VISIBLE_DOC_GROUPS = [
    'liegenschaft',
    'neubau_umbau',
    'stwe',
    'bauland',
];
/**
 * Doc requirements whose group is NOT owner-visible. Empty today, and the test
 * that asserts it is empty is the guard described above.
 *
 * Takes the registry as an argument rather than importing it: `pipeline.ts` is
 * product knowledge (D11) and this module is the portal contract, so the
 * dependency points the way the caller chooses instead of being baked in here.
 */
export function portalNonOwnerVisibleDocKeys(registry) {
    const visible = new Set(OWNER_VISIBLE_DOC_GROUPS);
    return registry.filter((d) => !visible.has(d.group)).map((d) => d.key);
}
// ---------------------------------------------------------------------------
// 4. Account-level API — outside Appendix L's case matrix, inside the portal
// ---------------------------------------------------------------------------
/**
 * Routes that belong to the ACCOUNT rather than to a case: the routing brain and
 * the F2d two-factor trio. They are customer-reachable on every surface, which
 * is why they cannot live in a per-surface capability row — and why they must
 * still be in the pinned inventory, or the deny-by-default contract test would
 * read them as an unclaimed widening.
 */
export const PORTAL_ACCOUNT_API = session('GET /me/context', 'GET /auth/customer/me', 'GET /auth/customer/totp', 'POST /auth/customer/totp/setup', 'POST /auth/customer/totp/confirm', 'POST /auth/customer/totp/disable', 
// DF-4 (owner-approved 13-08): the release reference. Session-any so that a
// report from a portal identity can carry WHICH build it saw — the
// dochter-verantwortliche of the feedback loop is a customer account
// (DRIP-FEEDBACK-LOOP.md §4.2/§6.2), and `artefaktRef` on her Eingabe is
// what makes §10.2's prod→staging→prod cycle readable. Account-level, not a
// case capability: build identity belongs to the SESSION, whatever the case.
// The measured cost — a signed-in customer reads the running sha — is the
// same fact every UAT invitation hands that person anyway. Anonymous stays
// 401: the route is session-guarded, only the @Roles list came off.
'GET /version');
/**
 * Every backend route an authenticated CUSTOMER session may reach, derived from
 * the matrix plus the account routes. Sorted and de-duplicated — a route may
 * legitimately serve two capabilities (the dossier section PUT is both the
 * checklist and the master-data edit).
 *
 * This is the list `portal-capabilities.contract.test.ts` compares against the
 * real controller tree. Anything reachable and not here fails the build; the
 * fix is to decide, not to append.
 */
export function portalCustomerApiRoutes() {
    const out = new Set();
    for (const surface of PORTAL_SURFACES) {
        for (const capability of PORTAL_CAPABILITIES) {
            for (const entry of PORTAL_CAPABILITY_MATRIX[surface][capability].api) {
                if (entry.auth === 'customer-session')
                    out.add(entry.route);
            }
        }
    }
    for (const entry of PORTAL_ACCOUNT_API)
        out.add(entry.route);
    return [...out].sort();
}
/**
 * Routes an external party reaches with a signed one-time token and NO session
 * (the pre-account Maklervertrag signing flow). Pinned separately because they
 * are `@Public()` in Nest terms: the session-based inventory above must not
 * expect them, and they must not therefore look unclaimed either.
 */
export function portalSignedTokenRoutes() {
    const out = new Set();
    for (const surface of PORTAL_SURFACES) {
        for (const capability of PORTAL_CAPABILITIES) {
            for (const entry of PORTAL_CAPABILITY_MATRIX[surface][capability].api) {
                if (entry.auth === 'signed-token')
                    out.add(entry.route);
            }
        }
    }
    return [...out].sort();
}
